/**
 * 계정 경제 - 핀 · 봉투 · 사물함을 **계정**이 들고 있는 값으로 다룬다.
 *
 * 근거 문서: 로드맵 확정안 3.1절(핀) · 3.2절(봉투 - 결정론) · 3.6절(페이월 금지 준수) · 고정 규칙 4·8
 *           `app/accountMagazines.ts` 머리말(`?mags=`가 `Tuning`에 없는 이유 - 여기 `?pins=`도 같다)
 *
 * ## 이 파일은 `accountMagazines.ts`의 짝이다
 *
 * 탄창에 입구가 셋(라운드가 쓴다 · 개발 훅이 준다 · `?mags=`가 덮는다)이라 모듈 하나로 묶은 것과 같은 이유로,
 * 핀에도 입구가 셋이다 - 라운드 결과가 준다(`earn`) · 봉투가 쓴다(`open`) · `?pins=`가 덮는다.
 * "저장을 건드리는가"를 판단하는 곳이 하나여야 한 줄만 새어 나가도 대조군이 진짜 값을 먹는 사고를 막는다.
 *
 * ## 규칙은 여기 없다
 *
 * 값을 얼마나 주는가(`pins.ts`) · 무엇이 나오는가(`envelope.ts`) · 그것이 무엇을 남기는가(`inventory.ts`)는
 * 전부 `game/economy/`의 순수 함수다. 이 파일은 그 셋을 **순서대로 부르고 한 덩어리로 저장**한다.
 * 봉투 한 장은 핀이 빠지고 · 회차가 오르고 · 산출물이 들어오는 세 변화라 `commitEconomy` 한 번으로 써야
 * "핀은 빠졌는데 산출물은 없다"가 남지 않는다(`progress.ts`의 `withEconomy` 주석).
 *
 * ## `?pins=`가 켜진 세션은 경제를 저장하지 않는다
 *
 * 핀만 강제하고 회차·사물함은 저장하면 저장이 **부분적으로** 진실이 된다 - 회차는 올랐는데 핀은 그대로인
 * 계정이 남고, 그 사람의 다음 봉투는 값을 내지 않은 것이 된다. 그래서 강제 중에는 셋 다 메모리에만 산다.
 * 탄창 지급은 `accountMagazines.grant`로 가는데 그쪽은 `?mags=`로 따로 판단한다 - 두 스위치가 독립인 것이
 * 의도다(`?pins=300`만 켜고 탄창 지급이 실제로 들어가는지 보는 것이 이 도구의 쓸모다).
 */

import { addPins, spendPins, type SpendResult } from '../game/economy/pins'
import {
  drawNext,
  pityBeforeSeq,
  pityOf,
  replayDraw,
  type DrawResult,
  type EnvelopeState,
  type Pity,
} from '../game/economy/envelope'
import { ENVELOPE_TABLE, TEN_PACK_SIZE, type EnvelopeTable } from '../game/economy/envelopeTable'
import { grantClearKit } from '../game/economy/kitDraft'
import type { KitId } from '../game/economy/kits'
import { applyReward, equipLook, equipNickname, useKit, type Inventory } from '../game/economy/inventory'
import type { LookId } from '../game/economy/looks'
import type { NicknameId } from '../game/economy/titles'
import { commitEconomy, type Progress } from '../game/progress'
import type { EnvelopeCardView } from '../ui/envelope'
import { resolvePinsOverride } from './urlState'

/** 한 번의 열기(한 장 또는 열 장)가 남긴 것. 봉투 화면(`ui/envelope.ts`)이 그리는 재료 전부다. */
export type OpenedEnvelopes = Readonly<{
  cards: readonly EnvelopeCardView[]
  /** 이번에 쓴 핀. 다시 보기는 0. */
  spent: number
  pinsBefore: number
  pinsAfter: number
  /** 첫 장을 열기 전의 피티. 다시 보기에서 그 회차의 앞 회차가 이력 밖이면 `null` - 모르는 값이고 화면은 게이지를 감춘다. */
  pityBefore: Pity | null
}>

export type AccountEconomy = Readonly<{
  /** 지금 보유 핀. `?pins=`로 강제된 세션이면 그 값에서 출발한다. */
  pins: () => number
  envelope: () => EnvelopeState
  inventory: () => Inventory
  /** 봉투 값. 표에서 그대로 읽는다 - 화면이 숫자를 따로 들지 않게. */
  price: () => Readonly<{ single: number; ten: number }>
  /** `?pins=`로 강제된 세션인가. 참이면 경제는 저장되지 않는다. */
  forced: boolean
  /** 라운드 결과의 핀을 더하고 저장한다. 음수·NaN은 `addPins`가 거른다. */
  earn: (amount: number) => void
  /** 지금 몇 장을 열 수 있는가. 거부 사유(`roundsToAfford`)까지 그대로 돌려준다. */
  canOpen: (count: 1 | 10) => SpendResult
  /** 봉투를 연다. 핀이 모자라면 `null`이고 상태는 한 핀도 움직이지 않는다. */
  open: (count: 1 | 10) => OpenedEnvelopes | null
  /** 특정 회차를 다시 계산해 보여 줄 재료를 만든다(`?pull=`). 저장을 건드리지 않는다. */
  replay: (seq: number) => OpenedEnvelopes
  /** 끼운다. 거부(안 가진 것)면 아무 일도 없다 - 판정은 `inventory.ts`의 것이다. */
  equipLook: (id: LookId) => void
  equipNickname: (id: NicknameId | null) => void
  /**
   * 준비물 하나를 쓴다(로드맵 M9 - 라운드 시작 시점). 없으면 거짓이고 아무것도 움직이지 않는다.
   * 판정도 산술도 `inventory.useKit`의 것이다 - 여기서 "0보다 큰가"를 다시 세면 보유 수가 두 곳에서 계산된다.
   */
  useKit: (id: KitId) => boolean
  /** 단계 클리어의 확정 지급(로드맵 M9 - 빈손이 이어지지 않게). 값은 `economy/kitDraft.grantClearKit`이 정한다. */
  grantClearKit: () => void
}>

export type AccountEconomyDeps = Readonly<{
  /** 주소창 질의문. `?pins=`만 읽는다. */
  search: string
  getProgress: () => Progress
  setProgress: (next: Progress) => void
  /** 탄창 지급(`accountMagazines.grant`). 실제로 들어간 수를 돌려준다. */
  grantMagazines: (count: number) => number
  /** 계정 탄창 수(`accountMagazines.count`). 상한까지 남은 자리를 셀 때 쓴다. */
  magazineCount: () => number
  table?: EnvelopeTable
}>

/**
 * 봉투 한 장을 카드 뷰로 만든다. `applyReward`가 계산한 것을 화면의 말로 옮기는 자리이고,
 * 여기서 "중복이면 25핀" 같은 값을 다시 계산하지 않는다 - 그 숫자는 `inventory.ts`에서 온 것을 그대로 쓴다.
 */
function toCard(
  draw: DrawResult,
  outcome: ReturnType<typeof applyReward>,
  magazinesGranted: number,
): EnvelopeCardView {
  const refunded = outcome.duplicate || outcome.overflowMagazines > 0
  return {
    draw,
    duplicate: outcome.duplicate,
    refundPins: refunded ? outcome.pinsToAdd : 0,
    overflowMagazines: outcome.overflowMagazines,
    magazinesGranted,
  }
}

export function createAccountEconomy(deps: AccountEconomyDeps): AccountEconomy {
  const table = deps.table ?? ENVELOPE_TABLE
  const override = resolvePinsOverride(deps.search)
  const forced = override !== undefined

  /*
   * 이번 세션이 믿는 값 셋. `progress`를 그대로 쓰지 않고 변수를 두는 이유는 강제 모드다(`accountMagazines.ts`와 같다) -
   * 강제 중에는 화면이 이 값을 따르고 저장은 원래 값을 그대로 들고 있어야 한다. 강제가 아니면 언제나 같은 값이다.
   */
  let pins = override ?? deps.getProgress().pins
  let envelope = deps.getProgress().envelope
  let inventory = deps.getProgress().inventory

  /**
   * 셋을 한 번에 저장한다. 강제 모드는 저장을 읽지도 쓰지도 않는다(머리말).
   *
   * **막히는 것은 핀만이 아니다.** 셋이 한 패치라 강제 세션에서는 사물함과 봉투 회차도 휘발한다 -
   * 준비물을 챙겨도 새로고침하면 돌아오고, 클리어 확정 지급도 남지 않는다. 그게 맞다: `?pins=`는
   * 연출을 보려고 켜는 대조군이고, 그 세션이 진짜 계정을 한 글자라도 고치면 도구가 아니라 사고다.
   * 이 사실은 `app/urlState.ts`의 표에도 함께 적혀 있다(적어 두지 않으면 다음 사람이 "준비물이
   * 안 빠진다"를 버그로 쫓는다).
   */
  function commit(): void {
    if (forced) return
    const committed = commitEconomy(deps.getProgress(), { pins, envelope, inventory })
    deps.setProgress(committed.progress)
    if (!committed.saved) console.warn('[main] 핀·봉투·사물함을 저장하지 못했다. 이번 세션에서는 그대로 간다.')
  }

  function costOf(count: 1 | 10): number {
    return count === 1 ? table.price.single : table.price.ten
  }

  function canOpen(count: 1 | 10): SpendResult {
    return spendPins(pins, costOf(count))
  }

  function open(count: 1 | 10): OpenedEnvelopes | null {
    const spend = canOpen(count)
    if (!spend.ok) return null

    const pinsBefore = pins
    const pityBefore = pityOf(envelope)
    let pinsAfter = spend.pins
    let nextEnvelope = envelope
    let nextInventory = inventory
    const cards: EnvelopeCardView[] = []

    for (let index = 0; index < (count === 1 ? 1 : TEN_PACK_SIZE); index += 1) {
      const drawn = drawNext(deps.getProgress().accountSeed, nextEnvelope, table)
      nextEnvelope = drawn.state
      const outcome = applyReward(nextInventory, drawn.result.reward, drawn.result.grade, deps.magazineCount(), table)
      nextInventory = outcome.inventory
      // 탄창은 계정 탄창 모듈이 넣는다. 실제로 들어간 수가 `applyReward`의 계산과 다르면 그 자체가 보고할 값이다.
      const granted = outcome.magazinesToGrant > 0 ? deps.grantMagazines(outcome.magazinesToGrant) : 0
      if (granted !== outcome.magazinesToGrant) {
        console.warn(`[main] 봉투 탄창 지급이 어긋났다(계산 ${String(outcome.magazinesToGrant)}, 실제 ${String(granted)}).`)
      }
      pinsAfter = addPins(pinsAfter, outcome.pinsToAdd)
      cards.push(toCard(drawn.result, outcome, granted))
    }

    pins = pinsAfter
    envelope = nextEnvelope
    inventory = nextInventory
    commit()

    return { cards, spent: costOf(count), pinsBefore, pinsAfter, pityBefore }
  }

  function replay(seq: number): OpenedEnvelopes {
    const draw = replayDraw(deps.getProgress().accountSeed, envelope, seq, table)
    // 다시 보기는 저장을 건드리지 않는다. 카드에 붙는 "이미 있어서"는 **지금** 사물함 기준이라 원래 그 회차와 다를 수 있다.
    const outcome = applyReward(inventory, draw.reward, draw.grade, deps.magazineCount(), table)
    return {
      cards: [toCard(draw, outcome, outcome.magazinesToGrant)],
      spent: 0,
      pinsBefore: pins,
      pinsAfter: pins,
      // "그 회차 직전의 피티"는 `replayDraw`와 같은 함수가 답한다 - 여기서 이력을 다시 뒤지면 조회가 두 벌이다.
      pityBefore: pityBeforeSeq(envelope, seq),
    }
  }

  return {
    pins: () => pins,
    envelope: () => envelope,
    inventory: () => inventory,
    price: () => table.price,
    forced,

    earn(amount: number): void {
      const next = addPins(pins, amount)
      if (next === pins) return
      pins = next
      commit()
    },

    canOpen,
    open,
    replay,

    equipLook(id: LookId): void {
      const next = equipLook(inventory, id)
      if (next === inventory) return
      inventory = next
      commit()
    },

    equipNickname(id: NicknameId | null): void {
      const next = equipNickname(inventory, id)
      if (next === inventory) return
      inventory = next
      commit()
    },

    useKit(id: KitId): boolean {
      const used = useKit(inventory, id)
      if (!used.ok) return false
      inventory = used.inventory
      commit()
      return true
    },

    grantClearKit(): void {
      inventory = grantClearKit(inventory)
      commit()
    },
  }
}
